<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.edit-regular"></svg-vue>
          <h1 class="content-title">
            {{ $t('Edit stock') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <button @click="deleteStockModal = true" class="btn btn-red rounded-none rounded-l-md" type="button">
            {{ $t('Delete') }}
          </button>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin/stocks">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <form @submit.prevent="updateStock">
          <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
            <div class="md:col-span-1">
              <h3 class="form-description-head">
                {{ $t('Edit product stock') }}
              </h3>
              <p class="form-description-text">{{ $t('This information will be displayed publicly') }}.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="grid grid-cols-3 gap-6">
                <div class="col-span-3">
                  <label class="flex justify-between block text-sm font-medium leading-5 text-gray-700" for="product_id">
                    {{ $t('Select product') }}
                  </label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <input-select id="product_id" v-model="product" :options="products" option-label="name" required searchable>
                      <template v-slot:selectedOption="props">
                        <template v-if="props && !props.anySelected">
                          <span class="block truncate">{{ $t('Select an option') }}</span>
                        </template>
                        <template v-else>
                          <div class="flex items-center space-x-3">
                            <div class="flex-shrink-0 inline-block">
                              <img
                                :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar"
                                alt="Icon"
                                class="w-5 h-auto rounded-full"
                              />
                            </div>
                            <div :class="props.option.id === product ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                              {{ props.option.name }}
                            </div>
                          </div>
                        </template>
                      </template>
                      <template v-slot:selectOption="props">
                        <div class="flex items-center space-x-3">
                          <div class="flex-shrink-0 inline-block">
                            <img
                              :src="props.option.avatar !== 'gravatar' ? props.option.avatar : props.option.gravatar"
                              alt="Icon"
                              class="w-5 h-auto rounded-full"
                            />
                          </div>
                          <div :class="props.option.id === product ? 'font-semibold' : 'font-normal'" class="font-normal block truncate">
                            {{ props.option.name }}
                          </div>
                        </div>
                      </template>
                    </input-select>
                  </div>
                </div>
                <div class="col-span-3" v-if="attributes.length > 0">
                  <label class="form-label" for="product_attribute_id">{{ $t('Attributes') }}</label>
                  <div class="pt-1">
                    <select class="form-input" id="product_attribute_id" required v-model="stock.product_attribute_id">
                      <option disabled :value="null">{{ $t('Select an option') }}</option>
                      <option v-for="(attribute, index) in attributes" :key="index" :value="attribute.id">{{ attribute.title }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="warehouse_id">{{ $t('Warehouse') }}</label>
                  <div class="pt-1">
                    <select class="form-input" id="warehouse_id" required v-model="stock.warehouse_id">
                      <option disabled :value="null">{{ $t('Select an option') }}</option>
                      <option v-for="(warehouse, index) in warehouses" :key="index" :value="warehouse.id">{{ warehouse.name }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="quantity">{{ $t('Quantity') }}</label>
                  <div class="pt-1">
                    <input :placeholder="$t('Quantity')" class="form-input" id="quantity" required type="number" v-model="stock.quantity" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <span class="inline-flex">
              <router-link class="btn btn-app mr-4" to="/admin/stocks">{{ $t('Cancel') }}</router-link>
              <button class="btn btn-green" type="submit">
                {{ $t('Update') }}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
    <stock-delete-model :show="deleteStockModal" @cancelled="deleteStockModal = false" @confirmed="deleteStock">
      <template #title>{{ $t('Delete stock') }}</template>
      {{ $t('Are you sure you want to delete the stock ?') }}
      {{ $t('All data will be permanently removed') }}.
    </stock-delete-model>
  </main>
</template>
<script>
import confirmationModel from '@/components/layout/admin/confirmation';
export default {
  name: 'edit',
  metaInfo() {
    return {
      title: this.$i18n.t('Edit stock'),
    };
  },
  components: {
    StockDeleteModel: confirmationModel,
  },
  data() {
    return {
      loading: true,
      deleteStockModal: false,
      products: [],
      product: null,
      attributes: [],
      warehouses: [],
      stock: {
        product_id: null,
        product_attribute_id: null,
        warehouse_id: null,
        rack_number: null,
        quantity: 0,
      },
    };
  },
  mounted() {
    this.getStockables();
    this.getStock();
  },
  watch: {
    product: {
      handler() {
        this.stock.product_id = this.product;
        const item = this.products.find((item) => item.id === this.stock.product_id);
        if (item.has_variants) {
          this.attributes = item.attributes;
        } else {
          this.attributes = [];
          this.stock.product_attribute_id = null;
        }
      },
      deep: true,
    },
  },
  methods: {
    getStockables() {
      this.loading = true;
      axios
        .get(route('stock.list'))
        .then((response) => {
          this.loading = false;
          this.products = response.data.products;
          this.warehouses = response.data.warehouses;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    updateStock() {
      this.loading = true;
      axios
        .put(route('stocks.update', this.$route.params.id), this.stock)
        .then((response) => {
          this.loading = false;
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getStock() {
      this.loading = true;
      axios
        .get(route('stocks.show', this.$route.params.id))
        .then((response) => {
          this.product = response.data.product_id;
          this.stock = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    deleteStock() {
      axios
        .delete(route('stocks.destroy', this.$route.params.id))
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.$router.push('/admin/stocks');
        })
        .catch(() => {
          this.deleteStockModal = false;
        });
    },
  },
};
</script>
